import { useEffect, useId, useRef, useState } from 'react'
import { Stethoscope } from 'lucide-react'
import Modal from '../../../components/ERP/Modal/Modal'
import { CalendarDatePicker } from '../../../components/ERP/Calendar/Calendar'
import TimePicker from '../../../components/ERP/TimePicker/TimePicker'
import { patientService } from '../../../services/patientApi'
import { authService } from '../../../services/api'
import { useAuth } from '../../../contexts/AuthContext'
import { ACTIONS, MODULES, usePermissions } from '../../../hooks/usePermissions'
import { councilStamp } from '../../../utils/professionalCouncil'
import { nowTimeInput, todayInput } from './patientFormat'
import './PatientsPage.css'

function noteClock(value) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date)
}

function notesText(notes) {
  return notes
    .map((note) => `${noteClock(note.created_at)} ${note.body}`.trim())
    .filter(Boolean)
    .join('\n')
}

function withoutChunk(value, chunk) {
  if (!chunk) return value
  return String(value || '').split(chunk).join('').replace(/\n{3,}/g, '\n\n').trim()
}

function AddEvolutionModal({ isOpen, patientId, onClose, onAdded }) {
  const { user } = useAuth()
  const { hasPermission } = usePermissions()
  const canImportNotes = hasPermission(MODULES.PATIENTS, ACTIONS.PATIENTS_NOTES) || hasPermission(MODULES.PATIENTS, ACTIONS.PATIENTS_NOTES_RECORD)
  const notesSwitchId = useId()
  const importedRef = useRef('')
  const [area, setArea] = useState(user?.access_group_name || '')
  const [registrationNumber, setRegistrationNumber] = useState('')
  const [profileStamp, setProfileStamp] = useState('')
  const [cid, setCid] = useState('')
  const [date, setDate] = useState(todayInput())
  const [time, setTime] = useState(nowTimeInput())
  const [subjective, setSubjective] = useState('')
  const [objective, setObjective] = useState('')
  const [assessment, setAssessment] = useState('')
  const [plan, setPlan] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [useShiftNotes, setUseShiftNotes] = useState(false)
  const [notesHint, setNotesHint] = useState('')

  useEffect(() => {
    if (!isOpen) return undefined
    let active = true
    const applyProfile = (source) => {
      if (!active || !source) return
      const stamp = councilStamp(source)
      if (stamp) {
        setProfileStamp(stamp)
        setRegistrationNumber(stamp)
      }
      if (source.access_group_name) setArea(source.access_group_name)
    }
    setDate(todayInput())
    setTime(nowTimeInput())
    applyProfile(user)
    authService.getProfile()
      .then((response) => applyProfile(response?.data))
      .catch(() => {})
    return () => {
      active = false
    }
  }, [isOpen, user])

  useEffect(() => {
    if (!isOpen) return
    setSubjective('')
    setUseShiftNotes(false)
    setNotesHint('')
    importedRef.current = ''
  }, [isOpen, patientId])

  const toggleShiftNotes = async (checked) => {
    if (!checked) {
      const chunk = importedRef.current
      importedRef.current = ''
      setUseShiftNotes(false)
      setNotesHint('')
      if (chunk) setSubjective((current) => withoutChunk(current, chunk))
      return
    }
    setNotesHint('')
    try {
      const response = await patientService.notes(patientId, { scope: 'shift' })
      const text = notesText(response.data || [])
      if (!text) {
        setUseShiftNotes(false)
        setNotesHint('Nenhuma nota do seu plantão neste paciente.')
        return
      }
      setSubjective((current) => {
        const base = withoutChunk(current, importedRef.current).trim()
        importedRef.current = text
        return base ? `${base}\n\n${text}` : text
      })
      setUseShiftNotes(true)
    } catch (err) {
      setUseShiftNotes(false)
      setNotesHint(err.response?.data?.message || 'Não foi possível importar as notas')
    }
  }

  const submit = async (event) => {
    event.preventDefault()
    if (!area) {
      setError('Seu usuário não tem grupo de acesso para registrar a área de atuação.')
      return
    }
    if (!date) {
      setError('Informe a data da evolução.')
      return
    }
    setSaving(true)
    setError('')
    try {
      await patientService.addEvolution(patientId, {
        registrationNumber, cid, date, time, subjective, objective, assessment, plan
      })
      onAdded()
      window.dispatchEvent(new Event('wfmed-clinical-updated'))
      onClose()
    } catch (err) {
      setError(err.response?.data?.message || 'Não foi possível salvar a evolução')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Nova evolução clínica"
      subtitle="Prontuário eletrônico (SOAP)"
      icon={<Stethoscope size={22} />}
      size="large"
      className="erp-modal--evolution"
      footer={(
        <button type="submit" form="evolution-form" disabled={saving || !area}>
          {saving ? 'Salvando...' : 'Assinar evolução'}
        </button>
      )}
    >
      <form id="evolution-form" onSubmit={submit}>
        {error && <p className="patients-alert-strip">{error}</p>}
        <div className="patients-form-grid patients-form-grid--3">
          <div>
            <label>Profissional responsável *</label>
            <input value={user?.name || ''} readOnly />
            <p className="patients-field-hint">Usuário do sistema</p>
          </div>
          <div>
            <label>Área de atuação</label>
            <input value={area} readOnly placeholder="Sem grupo de acesso" />
            <p className="patients-field-hint">Grupo de acesso do usuário</p>
          </div>
          <div>
            <label>Registro de classe</label>
            <input value={registrationNumber} onChange={(e) => setRegistrationNumber(e.target.value)} placeholder="CRM/SP 123456" maxLength={40} />
            {profileStamp && registrationNumber === profileStamp && (
              <p className="patients-field-hint">Conselho do perfil</p>
            )}
          </div>
          <div className="patients-evolution-date">
            <label>Data</label>
            <CalendarDatePicker
              value={date}
              onChange={setDate}
              title="Data da evolução"
              placeholder="Selecionar data"
            />
          </div>
          <div className="patients-evolution-time">
            <label>Hora</label>
            <TimePicker
              value={time}
              onChange={setTime}
              title="Hora da evolução"
              placeholder="Selecionar hora"
            />
          </div>
          <div>
            <label>CID-10</label>
            <input value={cid} onChange={(e) => setCid(e.target.value)} placeholder="Ex: J18.9" />
          </div>
          <div className="patients-span-3">
            {canImportNotes && (
              <div className="patients-notes-switch-row">
                <span>Notas do plantão</span>
                <div className="patients-notes-switch switch__container">
                  <input
                    id={notesSwitchId}
                    className="switch switch--shadow"
                    type="checkbox"
                    checked={useShiftNotes}
                    onChange={(event) => toggleShiftNotes(event.target.checked)}
                    aria-label="Trazer as notas do plantão para o subjetivo"
                  />
                  <label htmlFor={notesSwitchId} />
                </div>
              </div>
            )}
            <label>S — Subjetivo</label>
            <textarea value={subjective} onChange={(e) => setSubjective(e.target.value)} rows={2} />
            {notesHint && <p className="patients-field-hint">{notesHint}</p>}
          </div>
          <div className="patients-span-3"><label>O — Objetivo</label><textarea value={objective} onChange={(e) => setObjective(e.target.value)} rows={2} /></div>
          <div className="patients-span-3"><label>A — Avaliação clínica</label><textarea value={assessment} onChange={(e) => setAssessment(e.target.value)} rows={2} /></div>
          <div className="patients-span-3"><label>P — Plano e conduta</label><textarea value={plan} onChange={(e) => setPlan(e.target.value)} rows={2} /></div>
        </div>
      </form>
    </Modal>
  )
}

export default AddEvolutionModal
